import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  ClipboardList,
  Plus,
  Edit2,
  Trash2,
  Loader2,
  Camera,
  CheckCircle2,
  Copy,
  Archive,
  ArchiveRestore,
  FileText,
  GripVertical,
  ChevronUp,
  ChevronDown,
  AlertTriangle,
  Users,
  Settings,
  History,
  Download,
  Globe,
  Languages,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

import { API_BASE } from '../utils/apiClient';

const API = API_BASE;

const CRITICALITY_OPTIONS = [
  { value: 'faible', label: 'Faible', color: 'bg-slate-100 text-slate-800' },
  { value: 'moyenne', label: 'Moyenne', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'haute', label: 'Haute', color: 'bg-red-100 text-red-800' }
];

const FREQUENCY_OPTIONS = [
  { value: 'quotidien', label: 'Quotidien' },
  { value: 'hebdo', label: 'Hebdomadaire' }
];

// Types loaded dynamically from API - see fetchChecklistTypes()

const Checklists = () => {
  const { getAuthHeaders, isManager, isAdmin, isDemoReadonly } = useAuth();
  const { t, i18n } = useTranslation();
  const [checklists, setChecklists] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [archivedChecklists, setArchivedChecklists] = useState([]);
  const [zones, setZones] = useState([]);
  const [sites, setSites] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSite, setSelectedSite] = useState('all');
  const [activeTab, setActiveTab] = useState('checklists');
  
  // Checklist types (loaded from API)
  const [checklistTypes, setChecklistTypes] = useState([]);
  const [typesDialogOpen, setTypesDialogOpen] = useState(false);
  const [newTypeName, setNewTypeName] = useState('');
  const [savingType, setSavingType] = useState(false);
  
  // Translations dialog
  const [translationDialogOpen, setTranslationDialogOpen] = useState(false);
  const [checklistForTranslation, setChecklistForTranslation] = useState(null);
  const [translationLang, setTranslationLang] = useState('en');
  const [translationForm, setTranslationForm] = useState({ name: '', description: '', items: [] });
  const [savingTranslation, setSavingTranslation] = useState(false);
  const [autoTranslating, setAutoTranslating] = useState(false);

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [archivedListOpen, setArchivedListOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [editingChecklist, setEditingChecklist] = useState(null);
  const [checklistToDuplicate, setChecklistToDuplicate] = useState(null);
  const [checklistToArchive, setChecklistToArchive] = useState(null);
  const [checklistForHistory, setChecklistForHistory] = useState(null);
  const [versionHistory, setVersionHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Form state
  const [form, setForm] = useState({
    name: '',
    zone_id: '',
    frequency: 'quotidien',
    checklist_type: '5S',
    other_type_description: '', // For "Autre" type
    responsible_id: '',
    active: true,
    is_template: false,
    items: []
  });

  // Duplicate form
  const [duplicateForm, setDuplicateForm] = useState({
    new_name: '',
    zone_id: ''
  });

  // Item form
  const [itemForm, setItemForm] = useState({
    title: '',
    description: '',
    standard_expected: '',
    criticality: 'moyenne',
    photo_required: false
  });

  useEffect(() => {
    fetchData();
    fetchChecklistTypes();
  }, [i18n.language]);  // Reload when language changes

  const fetchChecklistTypes = async () => {
    try {
      const response = await axios.get(`${API}/checklist-types`, { headers: getAuthHeaders() });
      setChecklistTypes(response.data.types || []);
    } catch (error) {
      console.error('Error fetching checklist types:', error);
      // Fallback to default types
      setChecklistTypes(['5S', 'Sécurité', 'Qualité', 'Maintenance', 'Environnement', 'Production', 'Autre']);
    }
  };

  const handleAddType = async () => {
    if (!newTypeName.trim()) return;
    
    setSavingType(true);
    try {
      const response = await axios.post(`${API}/checklist-types/add`, 
        { type: newTypeName.trim() }, 
        { headers: getAuthHeaders() }
      );
      setChecklistTypes(response.data.types);
      setNewTypeName('');
      toast.success(`Type "${newTypeName}" ajouté`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'ajout');
    } finally {
      setSavingType(false);
    }
  };

  const handleDeleteType = async (typeName) => {
    if (typeName === 'Autre') {
      toast.error('Le type "Autre" ne peut pas être supprimé');
      return;
    }
    
    try {
      const response = await axios.delete(`${API}/checklist-types/${encodeURIComponent(typeName)}`, 
        { headers: getAuthHeaders() }
      );
      setChecklistTypes(response.data.types);
      toast.success(`Type "${typeName}" supprimé`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const fetchData = async () => {
    try {
      const lang = i18n.language || 'fr';
      const [checklistsRes, templatesRes, zonesRes, sitesRes, usersRes] = await Promise.all([
        axios.get(`${API}/checklists`, { params: { active_only: false, include_archived: false, lang }, headers: getAuthHeaders() }),
        axios.get(`${API}/checklists/templates`, { params: { lang }, headers: getAuthHeaders() }),
        axios.get(`${API}/zones`, { headers: getAuthHeaders() }),
        axios.get(`${API}/sites`, { headers: getAuthHeaders() }),
        axios.get(`${API}/users`, { headers: getAuthHeaders() })
      ]);
      setChecklists(checklistsRes.data.filter(c => !c.is_template));
      setTemplates(templatesRes.data);
      setZones(zonesRes.data);
      setSites(sitesRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const fetchArchivedChecklists = async () => {
    try {
      const lang = i18n.language || 'fr';
      const response = await axios.get(`${API}/checklists/archived`, { params: { lang }, headers: getAuthHeaders() });
      setArchivedChecklists(response.data);
    } catch (error) {
      console.error('Error fetching archived checklists:', error);
    }
  };

  const handleSave = async (forceEmpty = false) => {
    try {
      // Auto-add current item if title is filled but not yet added
      let itemsToSave = [...form.items];
      if (itemForm.title && itemForm.title.trim()) {
        const pendingItem = { 
          ...itemForm, 
          id: `temp-${Date.now()}`, 
          order: itemsToSave.length 
        };
        itemsToSave = [...itemsToSave, pendingItem];
        console.log('Auto-added pending item:', pendingItem.title);
      }
      
      // Warn if no items and not forcing
      if (itemsToSave.length === 0 && !forceEmpty) {
        toast.warning('⚠️ Cette checklist n\'a aucun point de contrôle. Ajoutez au moins un point avant d\'enregistrer.');
        return;
      }
      
      const payload = {
        ...form,
        items: itemsToSave,
        zone_id: form.is_template ? null : form.zone_id,
        responsible_id: form.responsible_id || null
      };

      console.log('Saving checklist with', payload.items.length, 'items');

      if (editingChecklist) {
        await axios.put(`${API}/checklists/${editingChecklist.id}`, payload, { headers: getAuthHeaders() });
        toast.success(`Checklist mise à jour avec ${payload.items.length} points de contrôle`);
      } else {
        await axios.post(`${API}/checklists`, payload, { headers: getAuthHeaders() });
        toast.success(form.is_template ? 'Modèle créé' : `Checklist créée avec ${payload.items.length} points de contrôle`);
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Save error:', error.response?.data || error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDuplicate = async () => {
    if (!checklistToDuplicate) return;
    
    try {
      const params = new URLSearchParams();
      if (duplicateForm.zone_id) params.append('zone_id', duplicateForm.zone_id);
      if (duplicateForm.new_name) params.append('new_name', duplicateForm.new_name);
      
      await axios.post(
        `${API}/checklists/${checklistToDuplicate.id}/duplicate?${params.toString()}`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success('Checklist dupliquée avec succès');
      setDuplicateDialogOpen(false);
      setChecklistToDuplicate(null);
      setDuplicateForm({ new_name: '', zone_id: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la duplication');
    }
  };

  const handleArchive = async (checklist, archive = true) => {
    try {
      await axios.patch(`${API}/checklists/${checklist.id}/archive?archive=${archive}`, {}, { headers: getAuthHeaders() });
      toast.success(archive ? 'Checklist archivée' : 'Checklist restaurée');
      setArchiveDialogOpen(false);
      setChecklistToArchive(null);
      fetchData();
      if (archivedListOpen) {
        fetchArchivedChecklists();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const openHistory = async (checklist) => {
    setChecklistForHistory(checklist);
    setHistoryDialogOpen(true);
    setLoadingHistory(true);
    try {
      const response = await axios.get(`${API}/checklists/${checklist.id}/versions`, { headers: getAuthHeaders() });
      setVersionHistory(response.data);
    } catch (error) {
      console.error('Error fetching history:', error);
      toast.error('Erreur lors du chargement de l\'historique');
    } finally {
      setLoadingHistory(false);
    }
  };

  const downloadHistory = async () => {
    if (!checklistForHistory) return;
    try {
      const response = await axios.get(`${API}/checklists/${checklistForHistory.id}/versions/export`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `historique_${checklistForHistory.name.replace(/\s+/g, '_')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Historique téléchargé');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleDelete = async (checklist) => {
    if (!window.confirm(`⚠️ ATTENTION: Supprimer définitivement "${checklist.name}" ?\n\nCette action est irréversible. Utilisez plutôt "Archiver" pour conserver l'historique.`)) return;
    try {
      await axios.delete(`${API}/checklists/${checklist.id}`, { headers: getAuthHeaders() });
      toast.success('Checklist supprimée');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const openEdit = (checklist) => {
    setEditingChecklist(checklist);
    setForm({
      name: checklist.name,
      zone_id: checklist.zone_id || '',
      frequency: checklist.frequency || 'quotidien',
      checklist_type: checklist.checklist_type || '5S',
      other_type_description: checklist.other_type_description || '',
      responsible_id: checklist.responsible_id || '',
      active: checklist.active !== false,
      is_template: checklist.is_template || false,
      items: (checklist.items || []).map((item, idx) => ({
        ...item,
        order: item.order ?? idx
      })).sort((a, b) => a.order - b.order)
    });
    setDialogOpen(true);
  };

  const openNew = (isTemplate = false) => {
    setEditingChecklist(null);
    resetForm();
    setForm(prev => ({ ...prev, is_template: isTemplate }));
    setDialogOpen(true);
  };

  const openDuplicateDialog = (checklist) => {
    setChecklistToDuplicate(checklist);
    setDuplicateForm({
      new_name: `${checklist.name} (copie)`,
      zone_id: checklist.zone_id || ''
    });
    setDuplicateDialogOpen(true);
  };

  const openArchiveDialog = (checklist) => {
    setChecklistToArchive(checklist);
    setArchiveDialogOpen(true);
  };

  const openArchivedList = () => {
    fetchArchivedChecklists();
    setArchivedListOpen(true);
  };

  const resetForm = () => {
    setForm({
      name: '',
      zone_id: '',
      frequency: 'quotidien',
      checklist_type: '5S',
      other_type_description: '',
      responsible_id: '',
      active: true,
      is_template: false,
      items: []
    });
    setItemForm({
      title: '',
      description: '',
      standard_expected: '',
      criticality: 'moyenne',
      photo_required: false
    });
  };

  const addItem = () => {
    if (!itemForm.title) return;
    setForm({
      ...form,
      items: [...form.items, { ...itemForm, id: `temp-${Date.now()}`, order: form.items.length }]
    });
    setItemForm({
      title: '',
      description: '',
      standard_expected: '',
      criticality: 'moyenne',
      photo_required: false
    });
  };

  const removeItem = (index) => {
    const newItems = [...form.items];
    newItems.splice(index, 1);
    // Reorder remaining items
    newItems.forEach((item, idx) => { item.order = idx; });
    setForm({ ...form, items: newItems });
  };

  const moveItem = (index, direction) => {
    const newItems = [...form.items];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= newItems.length) return;
    [newItems[index], newItems[newIndex]] = [newItems[newIndex], newItems[index]];
    newItems.forEach((item, idx) => { item.order = idx; });
    setForm({ ...form, items: newItems });
  };

  const updateItem = (index, updates) => {
    const newItems = [...form.items];
    newItems[index] = { ...newItems[index], ...updates };
    setForm({ ...form, items: newItems });
  };

  const getZoneName = (zoneId) => zones.find(z => z.id === zoneId)?.name || 'N/A';
  const getSiteName = (zoneId) => {
    const zone = zones.find(z => z.id === zoneId);
    if (!zone) return 'N/A';
    return sites.find(s => s.id === zone.site_id)?.name || 'N/A';
  };
  const getUserName = (userId) => users.find(u => u.id === userId)?.name || 'Non assigné';

  // Translation management
  const openTranslationDialog = async (checklist) => {
    setChecklistForTranslation(checklist);
    setTranslationLang('en');
    
    // Initialize form with existing translation or empty
    const existingTrans = checklist.translations?.[translationLang] || {};
    setTranslationForm({
      name: existingTrans.name || checklist.name,
      description: existingTrans.description || '',
      items: (checklist.items || []).map(item => ({
        item_id: item.id,
        title: item.translations?.[translationLang]?.title || item.title,
        description: item.translations?.[translationLang]?.description || item.description || '',
        standard_expected: item.translations?.[translationLang]?.standard_expected || item.standard_expected || '',
        hint: item.translations?.[translationLang]?.hint || item.hint || ''
      }))
    });
    
    setTranslationDialogOpen(true);
  };

  const handleTranslationLangChange = (lang) => {
    setTranslationLang(lang);
    if (checklistForTranslation) {
      const existingTrans = checklistForTranslation.translations?.[lang] || {};
      setTranslationForm({
        name: existingTrans.name || checklistForTranslation.name,
        description: existingTrans.description || '',
        items: (checklistForTranslation.items || []).map(item => ({
          item_id: item.id,
          title: item.translations?.[lang]?.title || item.title,
          description: item.translations?.[lang]?.description || item.description || '',
          standard_expected: item.translations?.[lang]?.standard_expected || item.standard_expected || '',
          hint: item.translations?.[lang]?.hint || item.hint || ''
        }))
      });
    }
  };

  const saveTranslation = async () => {
    if (!checklistForTranslation) return;
    
    setSavingTranslation(true);
    try {
      await axios.put(
        `${API}/checklists/${checklistForTranslation.id}/translations`,
        {
          lang: translationLang,
          name: translationForm.name,
          description: translationForm.description,
          items: translationForm.items
        },
        { headers: getAuthHeaders() }
      );
      toast.success(`Traduction ${translationLang.toUpperCase()} enregistrée`);
      setTranslationDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSavingTranslation(false);
    }
  };

  const updateTranslationItem = (index, field, value) => {
    const newItems = [...translationForm.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setTranslationForm({ ...translationForm, items: newItems });
  };

  const handleAutoTranslate = async () => {
    if (!checklistForTranslation) return;
    
    setAutoTranslating(true);
    try {
      const response = await axios.post(
        `${API}/checklists/${checklistForTranslation.id}/auto-translate`,
        { target_lang: translationLang },
        { headers: getAuthHeaders() }
      );
      
      const translated = response.data.translation;
      
      // Update form with translated content
      setTranslationForm({
        name: translated.name || '',
        description: translated.description || '',
        items: (checklistForTranslation.items || []).map((item, idx) => ({
          item_id: item.id,
          title: translated.items?.[idx]?.title || item.title,
          description: translated.items?.[idx]?.description || '',
          standard_expected: translated.items?.[idx]?.standard_expected || '',
          hint: translated.items?.[idx]?.hint || ''
        }))
      });
      
      toast.success(t('checklists.autoTranslateSuccess'));
    } catch (error) {
      console.error('Auto-translate error:', error);
      toast.error(error.response?.data?.detail || t('checklists.autoTranslateError'));
    } finally {
      setAutoTranslating(false);
    }
  };

  const getCriticalityBadge = (crit) => {
    const opt = CRITICALITY_OPTIONS.find(o => o.value === crit);
    return <Badge className={opt?.color || 'bg-slate-100'}>{opt?.label || crit}</Badge>;
  };

  const getTypeBadge = (type) => {
    const colors = {
      '5S': 'bg-blue-100 text-blue-800',
      'Sécurité': 'bg-red-100 text-red-800',
      'Qualité': 'bg-green-100 text-green-800',
      'Maintenance': 'bg-orange-100 text-orange-800',
      'Environnement': 'bg-emerald-100 text-emerald-800'
    };
    return <Badge className={colors[type] || 'bg-slate-100 text-slate-800'}>{type || 'Autre'}</Badge>;
  };

  // Filter checklists by site
  const filteredChecklists = selectedSite === 'all' 
    ? checklists 
    : checklists.filter(c => {
        const zone = zones.find(z => z.id === c.zone_id);
        return zone?.site_id === selectedSite;
      });

  const ChecklistCard = ({ checklist, showActions = true, isTemplate = false }) => (
    <Card 
      className={`border hover:shadow-md transition-shadow ${!checklist.active ? 'opacity-60' : ''}`} 
      data-testid={`checklist-card-${checklist.id}`}
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <CardTitle className="text-lg truncate">{checklist.name}</CardTitle>
              {isTemplate && <Badge className="bg-purple-100 text-purple-800">Modèle</Badge>}
            </div>
            {!isTemplate && (
              <>
                <p className="text-sm text-slate-500 mt-1">{getZoneName(checklist.zone_id)}</p>
                <p className="text-xs text-slate-400">{getSiteName(checklist.zone_id)}</p>
              </>
            )}
          </div>
          {!checklist.active && <Badge variant="secondary">Inactive</Badge>}
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          {getTypeBadge(checklist.checklist_type)}
          <Badge variant="outline">{checklist.frequency === 'quotidien' ? 'Quotidien' : 'Hebdo'}</Badge>
          <Badge variant="outline">{checklist.items?.length || 0} items</Badge>
        </div>

        {checklist.responsible_id && (
          <p className="text-xs text-slate-500 mb-2 flex items-center gap-1">
            <Users className="w-3 h-3" />
            {getUserName(checklist.responsible_id)}
          </p>
        )}

        {/* Items preview */}
        <div className="space-y-1 mb-4">
          {checklist.items?.slice(0, 3).map((item, idx) => (
            <div key={idx} className="flex items-center gap-2 text-sm text-slate-600">
              <CheckCircle2 className="w-3 h-3 text-slate-400 flex-shrink-0" />
              <span className="truncate">{item.title}</span>
              {item.photo_required && <Camera className="w-3 h-3 text-blue-500 flex-shrink-0" />}
              {item.criticality === 'haute' && <AlertTriangle className="w-3 h-3 text-red-500 flex-shrink-0" />}
            </div>
          ))}
          {checklist.items?.length > 3 && (
            <p className="text-xs text-slate-400">+{checklist.items.length - 3} autres...</p>
          )}
        </div>

        {showActions && isManager() && !isDemoReadonly() && (
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="default" size="sm" className="flex-1 bg-[#0E346E] hover:bg-[#0A2850]" onClick={() => openEdit(checklist)} data-testid={`edit-checklist-${checklist.id}`}>
              <Edit2 className="w-4 h-4 mr-1" />
              {t('common.edit')}
            </Button>
            <Button variant="outline" size="sm" onClick={() => openDuplicateDialog(checklist)} title={t('checklists.duplicate')} data-testid={`duplicate-btn-${checklist.id}`}>
              <Copy className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => openTranslationDialog(checklist)} title={t('checklists.translations')} data-testid={`translation-btn-${checklist.id}`}>
              <Globe className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => openHistory(checklist)} title={t('checklists.versionHistory')} data-testid={`history-btn-${checklist.id}`}>
              <History className="w-4 h-4" />
            </Button>
            {isAdmin() && (
              <Button variant="outline" size="sm" className="text-amber-600" onClick={() => openArchiveDialog(checklist)} title={t('checklists.archive')}>
                <Archive className="w-4 h-4" />
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 space-y-6 fade-in" data-testid="checklists-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-['Barlow_Condensed']">{t('checklists.title')}</h1>
          <p className="text-slate-500 mt-1">{t('checklists.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={selectedSite} onValueChange={setSelectedSite}>
            <SelectTrigger className="w-48" data-testid="site-filter">
              <SelectValue placeholder={t('common.allSites')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('common.allSites')}</SelectItem>
              {sites.map((site) => (
                <SelectItem key={site.id} value={site.id}>{site.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isAdmin() && (
            <Button variant="outline" size="sm" onClick={openArchivedList} className="text-slate-600" data-testid="view-archived-btn">
              <Archive className="w-4 h-4 mr-1" />
              {t('checklists.archives')}
            </Button>
          )}
          {isManager() && !isDemoReadonly() && (
            <Button onClick={() => openNew(false)} className="bg-[#0E346E] hover:bg-[#0A2850]" data-testid="add-checklist-btn">
              <Plus className="w-4 h-4 mr-2" />
              {t('checklists.newChecklist')}
            </Button>
          )}
        </div>
      </div>

      {/* Tabs for Checklists and Templates */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="checklists" className="flex items-center gap-2">
            <ClipboardList className="w-4 h-4" />
            Checklists ({filteredChecklists.length})
          </TabsTrigger>
          <TabsTrigger value="templates" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Modèles ({templates.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="checklists" className="mt-6">
          {filteredChecklists.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-16 text-center">
                <ClipboardList className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Aucune checklist créée</p>
                <p className="text-sm text-slate-400 mt-1">Créez une checklist ou dupliquez un modèle existant</p>
                {isManager() && (
                  <div className="flex items-center justify-center gap-2 mt-4">
                    <Button variant="outline" onClick={() => openNew(false)}>
                      <Plus className="w-4 h-4 mr-1" />
                      Créer une checklist
                    </Button>
                    {templates.length > 0 && (
                      <Button variant="outline" onClick={() => setActiveTab('templates')}>
                        <Copy className="w-4 h-4 mr-1" />
                        Utiliser un modèle
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredChecklists.map((checklist) => (
                <ChecklistCard key={checklist.id} checklist={checklist} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="templates" className="mt-6">
          <div className="mb-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
            <div className="flex items-start gap-3">
              <FileText className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-purple-900">Modèles de checklists</h3>
                <p className="text-sm text-purple-700 mt-1">
                  Les modèles sont des checklists réutilisables. Dupliquez-les vers une zone spécifique pour les utiliser.
                  Les modifications sur une checklist dupliquée ne modifient pas le modèle d'origine.
                </p>
              </div>
            </div>
          </div>

          {isManager() && (
            <div className="mb-4">
              <Button onClick={() => openNew(true)} variant="outline" className="border-purple-300 text-purple-700 hover:bg-purple-50" data-testid="add-template-btn">
                <Plus className="w-4 h-4 mr-2" />
                Créer un modèle
              </Button>
            </div>
          )}

          {templates.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-12 text-center">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Aucun modèle créé</p>
                {isManager() && (
                  <Button variant="outline" className="mt-4" onClick={() => openNew(true)}>
                    Créer un modèle
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map((template) => (
                <ChecklistCard key={template.id} checklist={template} isTemplate />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Checklist/Template Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="pb-4 border-b">
            <DialogTitle className="font-['Barlow_Condensed'] text-xl flex items-center gap-2">
              {form.is_template ? (
                <>
                  <FileText className="w-5 h-5 text-purple-600" />
                  {editingChecklist ? 'MODIFIER LE MODÈLE' : 'NOUVEAU MODÈLE'}
                </>
              ) : (
                <>
                  <ClipboardList className="w-5 h-5 text-blue-600" />
                  {editingChecklist ? 'MODIFIER LA CHECKLIST' : 'NOUVELLE CHECKLIST'}
                </>
              )}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="cl-name" className="text-sm font-semibold">Nom *</Label>
                <Input
                  id="cl-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Ex: Inspection 5S Quotidienne"
                  className="border-2"
                  data-testid="checklist-name-input"
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-semibold">Type</Label>
                  {isAdmin && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-6 text-xs"
                      onClick={() => setTypesDialogOpen(true)}
                    >
                      <Settings className="w-3 h-3 mr-1" />
                      Gérer les types
                    </Button>
                  )}
                </div>
                <Select value={form.checklist_type} onValueChange={(v) => setForm({ ...form, checklist_type: v, other_type_description: v !== 'Autre' ? '' : form.other_type_description })}>
                  <SelectTrigger className="border-2" data-testid="checklist-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {checklistTypes.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {form.checklist_type === 'Autre' && (
                  <Input
                    placeholder="Précisez le type de checklist..."
                    value={form.other_type_description}
                    onChange={(e) => setForm({ ...form, other_type_description: e.target.value })}
                    className="mt-2 border-2"
                    data-testid="other-type-description"
                  />
                )}
              </div>
            </div>

            {/* Zone selection (not for templates) */}
            {!form.is_template && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Zone *</Label>
                  <Select value={form.zone_id} onValueChange={(v) => setForm({ ...form, zone_id: v })}>
                    <SelectTrigger className="border-2" data-testid="checklist-zone-select">
                      <SelectValue placeholder="Sélectionner une zone" />
                    </SelectTrigger>
                    <SelectContent>
                      {zones.map((zone) => (
                        <SelectItem key={zone.id} value={zone.id}>
                          {zone.name} ({sites.find(s => s.id === zone.site_id)?.name})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Responsable</Label>
                  <Select value={form.responsible_id || 'none'} onValueChange={(v) => setForm({ ...form, responsible_id: v === 'none' ? '' : v })}>
                    <SelectTrigger className="border-2">
                      <SelectValue placeholder="Non assigné" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Non assigné</SelectItem>
                      {users.map(user => (
                        <SelectItem key={user.id} value={user.id}>{user.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Fréquence</Label>
                <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                  <SelectTrigger className="border-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FREQUENCY_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Statut</Label>
                <div className="flex items-center space-x-2 h-10">
                  <Switch
                    checked={form.active}
                    onCheckedChange={(v) => setForm({ ...form, active: v })}
                  />
                  <span className="text-sm text-slate-600">{form.active ? 'Active' : 'Inactive'}</span>
                </div>
              </div>
            </div>

            {/* Items Section */}
            <div className="border-t pt-4">
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                Points de contrôle ({form.items.length})
              </h3>

              {/* Existing Items */}
              {form.items.length > 0 && (
                <div className="space-y-2 mb-4">
                  {form.items.map((item, index) => (
                    <div key={item.id || index} className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg border">
                      <div className="flex flex-col gap-1">
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => moveItem(index, 'up')} disabled={index === 0}>
                          <ChevronUp className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => moveItem(index, 'down')} disabled={index === form.items.length - 1}>
                          <ChevronDown className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm truncate">{item.title}</span>
                          {getCriticalityBadge(item.criticality)}
                          {item.photo_required && <Camera className="w-4 h-4 text-blue-500" />}
                        </div>
                        {item.description && <p className="text-xs text-slate-500 truncate">{item.description}</p>}
                      </div>
                      <div className="flex items-center gap-1">
                        <Select value={item.criticality} onValueChange={(v) => updateItem(index, { criticality: v })}>
                          <SelectTrigger className="w-24 h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {CRITICALITY_OPTIONS.map(opt => (
                              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => updateItem(index, { photo_required: !item.photo_required })} title="Photo obligatoire">
                          <Camera className={`w-4 h-4 ${item.photo_required ? 'text-blue-600' : 'text-slate-400'}`} />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-500" onClick={() => removeItem(index)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Add New Item */}
              <div className="p-4 border-2 border-dashed rounded-lg bg-white">
                <h4 className="text-sm font-medium text-slate-700 mb-3">Ajouter un point de contrôle</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Input
                    value={itemForm.title}
                    onChange={(e) => setItemForm({ ...itemForm, title: e.target.value })}
                    placeholder="Titre du point *"
                    className="border-2"
                  />
                  <Input
                    value={itemForm.standard_expected}
                    onChange={(e) => setItemForm({ ...itemForm, standard_expected: e.target.value })}
                    placeholder="Standard attendu"
                    className="border-2"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                  <Textarea
                    value={itemForm.description}
                    onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })}
                    placeholder="Description (optionnel)"
                    className="border-2"
                    rows={2}
                  />
                  <div className="space-y-2">
                    <Label className="text-xs">Criticité</Label>
                    <Select value={itemForm.criticality} onValueChange={(v) => setItemForm({ ...itemForm, criticality: v })}>
                      <SelectTrigger className="border-2">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CRITICALITY_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end gap-2">
                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={itemForm.photo_required}
                        onCheckedChange={(v) => setItemForm({ ...itemForm, photo_required: v })}
                      />
                      <Label className="text-xs">Photo obligatoire</Label>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3 mt-3">
                  <Button variant="outline" onClick={addItem} disabled={!itemForm.title}>
                    <Plus className="w-4 h-4 mr-1" />
                    Ajouter
                  </Button>
                  {itemForm.title && (
                    <span className="text-sm text-amber-600 flex items-center gap-1">
                      <AlertTriangle className="w-4 h-4" />
                      Point en attente (sera ajouté automatiquement à l'enregistrement)
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="pt-4 border-t">
            <div className="flex items-center justify-between w-full">
              <div className="text-sm text-slate-500">
                {form.items.length + (itemForm.title ? 1 : 0)} point(s) de contrôle
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
                <Button 
                  onClick={() => handleSave(false)} 
                  disabled={!form.name || (!form.is_template && !form.zone_id)} 
                  className="bg-[#0E346E] hover:bg-[#0A2850]"
                  data-testid="save-checklist-btn"
                >
                  {editingChecklist ? 'Enregistrer les modifications' : (form.is_template ? 'Créer le modèle' : 'Créer la checklist')}
                </Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate Dialog */}
      <Dialog open={duplicateDialogOpen} onOpenChange={setDuplicateDialogOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader className="pb-4 border-b">
            <DialogTitle className="font-['Barlow_Condensed'] text-xl flex items-center gap-2">
              <Copy className="w-5 h-5 text-blue-600" />
              DUPLIQUER LA CHECKLIST
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-800">
              <p className="font-medium mb-1">💡 Duplication indépendante</p>
              <p>La nouvelle checklist sera totalement indépendante. Les modifications ne toucheront pas l'originale.</p>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Nouveau nom</Label>
              <Input
                value={duplicateForm.new_name}
                onChange={(e) => setDuplicateForm({ ...duplicateForm, new_name: e.target.value })}
                className="border-2"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Zone de destination *</Label>
              <Select value={duplicateForm.zone_id} onValueChange={(v) => setDuplicateForm({ ...duplicateForm, zone_id: v })}>
                <SelectTrigger className="border-2">
                  <SelectValue placeholder="Sélectionner une zone" />
                </SelectTrigger>
                <SelectContent>
                  {zones.map((zone) => (
                    <SelectItem key={zone.id} value={zone.id}>
                      {zone.name} ({sites.find(s => s.id === zone.site_id)?.name})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="pt-4 border-t">
            <Button variant="outline" onClick={() => setDuplicateDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleDuplicate} disabled={!duplicateForm.zone_id} className="bg-[#0E346E] hover:bg-[#0A2850]">
              <Copy className="w-4 h-4 mr-1" />
              Dupliquer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
              <Archive className="w-5 h-5" />
              Archiver la checklist
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>Vous allez archiver <strong>"{checklistToArchive?.name}"</strong>.</p>
                <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-800">
                  <p className="font-medium mb-1">Ce que l'archivage préserve :</p>
                  <ul className="list-disc list-inside space-y-1 text-blue-700">
                    <li>L'historique des tournées réalisées avec cette checklist</li>
                    <li>Les données et rapports passés</li>
                  </ul>
                </div>
                <div className="bg-amber-50 p-3 rounded-lg text-sm text-amber-800">
                  <p className="font-medium mb-1">Effet de l'archivage :</p>
                  <p>La checklist n'apparaîtra plus dans les tournées actives mais reste accessible dans les archives.</p>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleArchive(checklistToArchive, true)} className="bg-amber-600 hover:bg-amber-700">
              <Archive className="w-4 h-4 mr-1" />
              Archiver
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Archived Checklists List Dialog */}
      <Dialog open={archivedListOpen} onOpenChange={setArchivedListOpen}>
        <DialogContent className="bg-white max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader className="pb-4 border-b">
            <DialogTitle className="font-['Barlow_Condensed'] text-xl flex items-center gap-2">
              <Archive className="w-5 h-5 text-amber-600" />
              CHECKLISTS ARCHIVÉES
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {archivedChecklists.length === 0 ? (
              <div className="text-center py-8">
                <Archive className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">Aucune checklist archivée</p>
              </div>
            ) : (
              <div className="space-y-3">
                {archivedChecklists.map((checklist) => (
                  <div key={checklist.id} className="flex items-center justify-between p-3 border rounded-lg bg-slate-50">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <ClipboardList className="w-8 h-8 text-slate-400 flex-shrink-0" />
                      <div className="min-w-0">
                        <h4 className="font-medium text-slate-700 truncate">{checklist.name}</h4>
                        <p className="text-xs text-slate-400">{getZoneName(checklist.zone_id)}</p>
                        {checklist.archived_at && (
                          <p className="text-xs text-slate-400">
                            Archivé le {new Date(checklist.archived_at).toLocaleDateString('fr-FR')}
                          </p>
                        )}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-green-600 border-green-300 hover:bg-green-50"
                      onClick={() => handleArchive(checklist, false)}
                    >
                      <ArchiveRestore className="w-4 h-4 mr-1" />
                      Restaurer
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter className="pt-4 border-t">
            <Button variant="outline" onClick={() => setArchivedListOpen(false)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Version History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader className="pb-4 border-b">
            <DialogTitle className="font-['Barlow_Condensed'] text-xl flex items-center gap-2">
              <History className="w-5 h-5 text-blue-600" />
              HISTORIQUE DES VERSIONS
            </DialogTitle>
            {checklistForHistory && (
              <p className="text-sm text-slate-500 mt-1">
                Checklist : {checklistForHistory.name}
              </p>
            )}
          </DialogHeader>
          
          <div className="py-4">
            {loadingHistory ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : versionHistory.length === 0 ? (
              <div className="text-center py-8">
                <History className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">Aucune version précédente</p>
                <p className="text-sm text-slate-400 mt-1">
                  L'historique sera créé lors des prochaines modifications
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Current version indicator */}
                {checklistForHistory && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge className="bg-green-600 text-white">V{checklistForHistory.version || 1}</Badge>
                        <span className="font-medium text-green-800">Version actuelle</span>
                      </div>
                      <span className="text-xs text-green-600">En production</span>
                    </div>
                    <p className="text-sm text-green-700 mt-1">{checklistForHistory.items?.length || 0} items</p>
                  </div>
                )}

                {/* Historical versions */}
                {versionHistory.map((version) => (
                  <div 
                    key={version.id} 
                    className="p-3 border rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                    data-testid={`version-item-${version.version}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline" className="text-slate-600">V{version.version}</Badge>
                          <span className="font-medium text-slate-700">{version.name}</span>
                        </div>
                        <div className="text-xs text-slate-500 space-y-0.5">
                          <p>
                            Modifié le {new Date(version.created_at).toLocaleDateString('fr-FR', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                          <p>Par : {version.created_by_name}</p>
                          <p>{version.items?.length || 0} items • {version.frequency === 'quotidien' ? 'Quotidien' : 'Hebdomadaire'}</p>
                        </div>
                        {version.change_summary && (
                          <p className="text-xs text-slate-600 mt-1 italic">{version.change_summary}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter className="pt-4 border-t flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={downloadHistory}
              disabled={versionHistory.length === 0}
              className="w-full sm:w-auto"
              data-testid="download-history-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Télécharger CSV
            </Button>
            <Button 
              variant="default" 
              onClick={() => setHistoryDialogOpen(false)}
              className="w-full sm:w-auto bg-[#0E346E] hover:bg-[#0A2850]"
            >
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Types Management Dialog */}
      <Dialog open={typesDialogOpen} onOpenChange={setTypesDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Gérer les types de checklist
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Add new type */}
            <div className="flex gap-2">
              <Input
                placeholder="Nouveau type..."
                value={newTypeName}
                onChange={(e) => setNewTypeName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddType()}
                className="flex-1"
                data-testid="new-type-input"
              />
              <Button 
                onClick={handleAddType} 
                disabled={savingType || !newTypeName.trim()}
                className="bg-[#0E346E] hover:bg-[#0A2850]"
              >
                {savingType ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              </Button>
            </div>
            
            {/* List of types */}
            <div className="border rounded-lg divide-y max-h-64 overflow-y-auto">
              {checklistTypes.map((type) => (
                <div key={type} className="flex items-center justify-between p-3 hover:bg-slate-50">
                  <span className="font-medium">{type}</span>
                  {type !== 'Autre' ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-800 hover:bg-red-50"
                      onClick={() => handleDeleteType(type)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  ) : (
                    <Badge variant="outline" className="text-xs">Requis</Badge>
                  )}
                </div>
              ))}
            </div>
            
            <p className="text-xs text-slate-500">
              Le type "Autre" ne peut pas être supprimé. Les nouveaux types sont ajoutés avant "Autre".
            </p>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setTypesDialogOpen(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Translation Dialog */}
      <Dialog open={translationDialogOpen} onOpenChange={setTranslationDialogOpen}>
        <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="pb-4 border-b">
            <DialogTitle className="font-['Barlow_Condensed'] text-xl flex items-center gap-2">
              <Languages className="w-5 h-5 text-blue-600" />
              {t('checklists.manageTranslations')}
            </DialogTitle>
            {checklistForTranslation && (
              <p className="text-sm text-slate-500 mt-1">
                {t('checklists.originalName')}: {checklistForTranslation.name}
              </p>
            )}
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Language selector */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Label className="font-semibold">{t('checklists.targetLanguage')}:</Label>
                <div className="flex gap-2">
                  <Button 
                    variant={translationLang === 'en' ? 'default' : 'outline'} 
                    size="sm"
                    onClick={() => handleTranslationLangChange('en')}
                    className={translationLang === 'en' ? 'bg-[#0E346E]' : ''}
                  >
                    🇬🇧 English
                  </Button>
                  <Button 
                    variant={translationLang === 'es' ? 'default' : 'outline'} 
                    size="sm"
                    onClick={() => handleTranslationLangChange('es')}
                    className={translationLang === 'es' ? 'bg-[#0E346E]' : ''}
                  >
                    🇪🇸 Español
                  </Button>
                </div>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleAutoTranslate}
                disabled={autoTranslating}
                className="text-purple-600 border-purple-300 hover:bg-purple-50"
              >
                {autoTranslating ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-2" />
                )}
                {t('checklists.autoTranslate')}
              </Button>
            </div>

            {/* Checklist name translation */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">{t('checklists.name')} ({translationLang.toUpperCase()})</Label>
              <Input
                value={translationForm.name}
                onChange={(e) => setTranslationForm({ ...translationForm, name: e.target.value })}
                placeholder={`${t('checklists.name')} in ${translationLang.toUpperCase()}`}
                className="border-2"
              />
            </div>

            {/* Items translations */}
            <div className="border-t pt-4">
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                {t('checklists.itemsTranslations')} ({translationForm.items?.length || 0})
              </h3>

              <div className="space-y-4">
                {translationForm.items?.map((item, index) => (
                  <div key={item.item_id} className="p-4 bg-slate-50 rounded-lg border">
                    <div className="text-xs text-slate-500 mb-2">
                      {t('checklists.originalTitle')}: <span className="font-medium">{checklistForTranslation?.items?.[index]?.title}</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">{t('checklists.translatedTitle')}</Label>
                        <Input
                          value={item.title}
                          onChange={(e) => updateTranslationItem(index, 'title', e.target.value)}
                          placeholder={`Title in ${translationLang.toUpperCase()}`}
                          className="border"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">{t('checklists.standardExpected')}</Label>
                        <Input
                          value={item.standard_expected}
                          onChange={(e) => updateTranslationItem(index, 'standard_expected', e.target.value)}
                          placeholder={`Standard in ${translationLang.toUpperCase()}`}
                          className="border"
                        />
                      </div>
                    </div>
                    <div className="mt-2">
                      <Label className="text-xs">{t('checklists.description')}</Label>
                      <Textarea
                        value={item.description}
                        onChange={(e) => updateTranslationItem(index, 'description', e.target.value)}
                        placeholder={`Description in ${translationLang.toUpperCase()}`}
                        className="border mt-1"
                        rows={2}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter className="pt-4 border-t">
            <Button variant="outline" onClick={() => setTranslationDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={saveTranslation} 
              disabled={savingTranslation || !translationForm.name}
              className="bg-[#0E346E] hover:bg-[#0A2850]"
            >
              {savingTranslation ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Globe className="w-4 h-4 mr-2" />
              )}
              {t('checklists.saveTranslation')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Checklists;
